import { createSelector } from 'reselect';

import {
    MULTIMODAL_TYPE,
    FEATURE_IS_ACTIVE_DEFAULT,
    STATION_IS_ACTIVE_DEFAULT,
} from '../filterData/constants';
import {
    selectFilterQueryParams as _selectFilterQueryParams,
    selectFilterId,
    selectActiveSortType as _selectActiveSortType,

    selectAllowNearbyFilterData,
    selectCarriersFilterData,
    selectDirectOnlyFilterData,
    selectFeaturesFilterData,
    selectTimingFilterData,
    selectTravelModesFilterData,
    selectStationsFilterData,

    selectAllowNearbyIsFiltered as _selectAllowNearbyIsFiltered,
    selectCarriersIsFiltered as _selectCarriersIsFiltered,
    selectDirectOnlyIsFiltered as _selectDirectOnlyIsFiltered,
    selectFeaturesIsFiltered as _selectFeaturesIsFiltered,
    selectDepartureTimingIsFiltered as _selectDepartureTimingIsFiltered,
    selectArrivalTimingIsFiltered as _selectArrivalTimingIsFiltered,
    selectTravelModesIsFiltered as _selectTravelModesIsFiltered,
    selectStationsIsFiltered as _selectStationsIsFiltered,
} from '../filterData/selectors';
import {
    selectSortOptions,
    selectCarriersByMode,
    selectCarrierModesMap,
    selectTravelModes,

    selectAllowNearbyTripData,
    selectCarriersTripData,
    selectStationsTripData,
    selectDirectOnlyTripData,
    selectFeaturesTripData,
    selectTravelModesTripData,
} from '../trips/selectors';

function selectAllowNearbyFilters(state, searchParams) {
    const filterId = selectFilterId(state, searchParams);

    return {
        ...selectAllowNearbyFilterData(state, filterId),
        ...selectAllowNearbyTripData(state, searchParams),
    };
}

function selectDirectOnlyFilters(state, searchParams) {
    const filterId = selectFilterId(state, searchParams);
    return {
        ...selectDirectOnlyFilterData(state, filterId),
        ...selectDirectOnlyTripData(state, searchParams),
    };
}

function selectCarrierFilters(state, searchParams) {
    const filterId = selectFilterId(state, searchParams);

    const carriersFilterData = selectCarriersFilterData(state, filterId);
    const allowNearbyIsFiltered = selectAllowNearbyIsFiltered(state, searchParams);
    const carriersTripData = selectCarriersTripData(
        state,
        searchParams,
        allowNearbyIsFiltered,
    );

    return Object.entries(carriersTripData).reduce((map, [carrierCode, travelModesObj]) => {
        return Object.entries(travelModesObj).reduce((innerMap, [travelMode, tripData]) => {
            const carrierData = carriersFilterData[carrierCode] || {};
            const filterData = carrierData[travelMode] || {};
            return {
                ...innerMap,
                [carrierCode]: {
                    ...innerMap[carrierCode] || {},
                    [travelMode]: {
                        ...tripData,
                        ...filterData,
                    },
                },
            };
        }, map);
    }, {});
}

function hasTripModes(station, travelModesFilterData) {
    return station && station.tripModes.some((mode) => travelModesFilterData[mode]
        ? travelModesFilterData[mode].state
        : true);
}

function mapFilterData(
    filterData,
    tripData,
    allowNearbyFilterData,
    travelModesFilterData,
) {
    return Object.entries(filterData).reduce(
        (map, [stationId, { isActive }]) => {
            if (
                tripData[stationId]
                && !tripData[stationId].exactMatch
                && !allowNearbyFilterData.isActive
            ) return map;
            if (!hasTripModes(tripData[stationId], travelModesFilterData)) return map;
            return map.concat({
                ...tripData[stationId],
                id: stationId,
                isActive:
                    isActive !== undefined
                        ? isActive
                        : STATION_IS_ACTIVE_DEFAULT,
            });
        },
        [],
    );
}

function selectStationFilters(state, searchParams) {
    const filterId = selectFilterId(state, searchParams);
    const {
        arrival: arriveFilterData,
        departure: departFilterData,
    } = selectStationsFilterData(state, filterId);
    const {
        arrival: arriveTripData,
        departure: departTripData,
    } = selectStationsTripData(state, searchParams);
    const allowNearbyFilterData = selectAllowNearbyFilterData(state, filterId);
    const travelModeFilterData = selectTravelModesFilterData(state, filterId);
    const arrival = mapFilterData(
        arriveFilterData,
        arriveTripData,
        allowNearbyFilterData,
        travelModeFilterData,
    );
    const departure = mapFilterData(
        departFilterData,
        departTripData,
        allowNearbyFilterData,
        travelModeFilterData,
    );
    return {
        arrival,
        departure,
    };
}

function selectFeatureFilters(state, searchParams) {
    const filterId = selectFilterId(state, searchParams);

    const featuresFilterData = selectFeaturesFilterData(state, filterId);
    const featuresTripData = selectFeaturesTripData(state, searchParams);

    return featuresTripData.map((featureId) => {
        const filterData = featuresFilterData[featureId] || {};
        return {
            featureId,
            ...filterData,
            // TODO: features could be expanded and stored in redux to avoid this
            isActive: filterData.isActive || FEATURE_IS_ACTIVE_DEFAULT,
        };
    }, {});
}

function selectTimingFilters(state, searchParams) {
    const filterId = selectFilterId(state, searchParams);

    return selectTimingFilterData(state, filterId);
}

function sortTravelModes([travelModeA], [travelModeB]) {
    if (travelModeA === MULTIMODAL_TYPE) {
        return 1;
    }

    if (travelModeB === MULTIMODAL_TYPE) {
        return -1;
    }

    return travelModeA.localeCompare(travelModeB);
}

function selectTravelModesFilters(state, searchParams) {
    const filterId = selectFilterId(state, searchParams);

    const travelModesFilterData = selectTravelModesFilterData(state, filterId);
    const allowNearbyIsFiltered = selectAllowNearbyIsFiltered(state, searchParams);
    const travelModesTripData = selectTravelModesTripData(
        state,
        searchParams,
        allowNearbyIsFiltered,
    );
    return Object.entries(travelModesTripData)
        .sort(sortTravelModes)
        .map(([travelMode, tripData]) => {
            const filterData = travelModesFilterData[travelMode];

            return {
                ...filterData,
                ...tripData,
            };
        });
}
const selectFilters = createSelector(
    selectAllowNearbyFilters,
    selectCarrierFilters,
    selectDirectOnlyFilters,
    selectFeatureFilters,
    selectTimingFilters,
    selectTravelModesFilters,
    selectStationFilters,
    (...filters) => ({
        allowNearby: filters[0],
        carriers: filters[1],
        directOnly: filters[2],
        features: filters[3],
        stations: filters[6],
        timing: filters[4],
        travelModes: filters[5],
    }),
);

function selectTravelModesIsFiltered(state, searchParams) {
    const filterId = selectFilterId(state, searchParams);
    return _selectTravelModesIsFiltered(state, filterId);
}

function selectCarriersIsFiltered(state, searchParams) {
    const filterId = selectFilterId(state, searchParams);
    return _selectCarriersIsFiltered(state, filterId);
}

function selectFeaturesIsFiltered(state, searchParams) {
    const filterId = selectFilterId(state, searchParams);
    return _selectFeaturesIsFiltered(state, filterId);
}

function selectDepartureTimingIsFiltered(state, searchParams) {
    const filterId = selectFilterId(state, searchParams);
    return _selectDepartureTimingIsFiltered(state, filterId);
}

function selectArrivalTimingIsFiltered(state, searchParams) {
    const filterId = selectFilterId(state, searchParams);
    return _selectArrivalTimingIsFiltered(state, filterId);
}

function selectDirectOnlyIsFiltered(state, searchParams) {
    const filterId = selectFilterId(state, searchParams);
    return _selectDirectOnlyIsFiltered(state, filterId);
}

function selectAllowNearbyIsFiltered(state, searchParams) {
    const filterId = selectFilterId(state, searchParams);
    return _selectAllowNearbyIsFiltered(state, filterId);
}

function selectArriveStationsIsFiltered(state, searchParams) {
    const filterId = selectFilterId(state, searchParams);
    return _selectStationsIsFiltered(state, filterId).arrival;
}

function selectDepartStationsIsFiltered(state, searchParams) {
    const filterId = selectFilterId(state, searchParams);
    return _selectStationsIsFiltered(state, filterId).departure;
}

const selectNumberOfActiveFilters = createSelector(
    selectTravelModesIsFiltered,
    selectCarriersIsFiltered,
    selectFeaturesIsFiltered,
    selectDepartureTimingIsFiltered,
    selectArrivalTimingIsFiltered,
    selectDirectOnlyIsFiltered,
    selectAllowNearbyIsFiltered,
    selectArriveStationsIsFiltered,
    selectDepartStationsIsFiltered,
    (...isFilteredData) => isFilteredData.filter(Boolean).length,
);

function selectActiveSortType(state, searchParams) {
    const filterId = selectFilterId(state, searchParams);

    return _selectActiveSortType(state, filterId);
}

function selectFilterQueryParams(state, searchParams) {
    const filterId = selectFilterId(state, searchParams);

    return _selectFilterQueryParams(state, filterId, searchParams.direction);
}

function selectFilterInfo(state, searchParams) {
    const filterId = selectFilterId(state, searchParams);
    const carrierModesMap = selectCarrierModesMap(state, searchParams);
    const travelModes = selectTravelModes(state, searchParams);
    const carriersByMode = selectCarriersByMode(state, searchParams);
    const stations = selectStationsTripData(state, searchParams);
    return {
        carrierModesMap,
        carriersByMode,
        filterId,
        stations,
        travelModes,
    };
}

export {
    selectActiveSortType,
    selectAllowNearbyIsFiltered,
    selectArrivalTimingIsFiltered,
    selectArriveStationsIsFiltered,
    selectCarriersIsFiltered,
    selectDepartStationsIsFiltered,
    selectDepartureTimingIsFiltered,
    selectDirectOnlyIsFiltered,
    selectFeaturesIsFiltered,
    selectFilterInfo,
    selectFilterQueryParams,
    selectFilters,
    selectNumberOfActiveFilters,
    selectSortOptions,
    selectStationFilters,
    selectTravelModesIsFiltered,
};
